import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { Roles } from '@/_shared/constants/roles';
import { GenericGet } from '@/_shared/types';
import { getCurrentOrganization } from '@/core/state/selectors';
import { SortOrder } from '@/swagger/constants/slash-enums';
import { API_ORGANIZATIONS_ORGANIZATIONID_DELEGATIONS_ASSIGNEES_PATH_KEY } from '@/swagger/constants/slash-keys';
import { API_GET_DELEGATES, KEY_GET_DELEGATES } from '../constants';
import { MOCK_RESPONSE_GET_DELEGATES } from '../mocks';
import { User } from '../types';

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetDelegates, 'successSaga' | 'errorSaga'>;
};

type RequestGetDelegates = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_DELEGATIONS_ASSIGNEES_PATH_KEY
>['request']['query'];

type ResponseGetDelegates = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_DELEGATIONS_ASSIGNEES_PATH_KEY
>['response']['body'];

export type CallParamsGetDelegates = RequestGetDelegates & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetDelegates = ResponseGetDelegates;

type CallErrorGetDelegates = {
  message: string;
  code: number;
};

type CallSelectorsGetDelegates = {
  admins: User[];
  hrDelegates: User[];
  fullRequest: FullRequestType;
};

let internalPayload = {};

export const getDelegatesCall = createGeneralCall<
  CallParamsGetDelegates,
  CallResponseGetDelegates,
  CallErrorGetDelegates,
  CallSelectorsGetDelegates
>({
  id: KEY_GET_DELEGATES,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      const payloadWithDefaultSort = {
        ...payload,
        orderBy: payload?.orderBy || {
          firstName: SortOrder.ASC,
        },
      };

      return API_GET_DELEGATES(org as unknown as string, payloadWithDefaultSort);
    },
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  method: 'get',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_DELEGATES : undefined,
  selectors: {
    admins: ({ selectors }) =>
      createSelector(
        selectors.response,
        res =>
          res?.data
            ?.filter(users => (users.roles as Roles[]).includes(Roles.Admin))
            .map(users => ({ ...users, roles: users.roles as Roles[] })) || [],
      ),
    hrDelegates: ({ selectors }) =>
      createSelector(
        selectors.response,
        res =>
          res?.data
            ?.filter(users => (users.roles as Roles[]).includes(Roles.HR_Delegate))
            .map(users => ({ ...users, roles: users.roles as Roles[] })) || [],
      ),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
});
